import IGlossaryTerm from '@/components/interfaces/GlossaryTerm';
import { plato_api_get_glossary_terms_glossary_terms_path } from '@/modules/routes';
import axios, { AxiosResponse } from 'axios';
import FE from 'froala-editor';
import ReactDOM from 'react-dom';
import React from 'react';
import { FROALA_TEMPLATE_NAMES } from '../../froalaUtils';
import { icon } from './icon';
import {
  cleanUpDeleteButton,
  fetchEdition,
  lessonOpenerCardsHtml,
  lessonOpenerListHtml,
  refreshTermsInEditor,
  sortedTerms,
  vocabBoxHtml,
} from './vocabBoxUtils';
import DeleteVocabDisplay from './DeleteVocabDisplay';

type ApiResponse = { glossary_terms: IGlossaryTerm[] };

export const VOCAB_BOX = {
  academicGlossaryTerms: [] as IGlossaryTerm[],
  commandName: 'VocabBox' as const,
  events: {
    addLessonOpenerCards: ({ editor, locale = 'en' }: {
      editor: FE,
      locale: Locale,
    }) => {
      editor.html.insert(lessonOpenerCardsHtml(VOCAB_BOX.glossaryTerms, locale), true);
    },
    addLessonOpenerList: ({ academic = false, editor, locale = 'en' }: {
      academic?: boolean,
      editor: FE,
      locale: Locale,
    }) => {
      const terms = academic ? VOCAB_BOX.academicGlossaryTerms : VOCAB_BOX.glossaryTerms;

      editor.html.insert(lessonOpenerListHtml(terms, locale, academic), true);
    },
    addVocabBox: async ({ editor, lessonId, locale = 'en', programId }: {
      editor: FE,
      lessonId: number | string,
      locale: Locale,
      programId: number | string
    }) => {
      if (!VOCAB_BOX.glossaryTerms || VOCAB_BOX.glossaryTerms.length === 0) return;

      const edition = await fetchEdition(programId);
      const html = vocabBoxHtml(edition, lessonId, locale, programId, VOCAB_BOX.glossaryTerms);

      editor.html.insert(html, true);
    },
    init: async (lessonId: string | number, editor: FE, selector: string, locale: Locale) => {
      if (!lessonId) return;

      let res: AxiosResponse<ApiResponse> | null;

      try {
        res = await axios
          .get<ApiResponse>(plato_api_get_glossary_terms_glossary_terms_path({
          lesson_id: lessonId,
        }));
      } catch {
        return;
      }

      // AIDEV-NOTE: PLAT-17813 — fetch every kind once and split here. `kind !== 'Academic'` mirrors
      // GlossaryTerm.lesson_glossary (`kind IN ('Lesson', NULL)`), so nil-kind terms stay with Lesson.
      const allTerms = res.data.glossary_terms;

      VOCAB_BOX.glossaryTerms = sortedTerms(allTerms.filter(term => term.kind !== 'Academic'), locale);
      VOCAB_BOX.academicGlossaryTerms = sortedTerms(allTerms.filter(term => term.kind === 'Academic'), locale);

      setTimeout(() => {
        refreshTermsInEditor(VOCAB_BOX.glossaryTerms, {
          academicTerms: VOCAB_BOX.academicGlossaryTerms,
          locale,
          selector,
        });
      }, 10);

      editor.html.set(editor.html.get());
      editor.undo.saveStep();
    },
    mousedown: (e: MouseEvent, editor: FE) => {
      const targetElement = e.target as HTMLElement;
      const vocabElement = targetElement.closest('.loc-grid, .loc-list, .vocab-box') as HTMLElement | null;

      if (!vocabElement) return;
      if (!editor || targetElement.className.includes('btn')) return;

      e.preventDefault();
      e.stopPropagation();

      const containerIdMap: Record<string, string> = {
        'loc-grid': 'lessonOpenerCardGridDeleteContainer',
        'loc-list': 'lessonOpenerListDeleteContainer',
        'vocab-box': 'vocabBoxDeleteContainer',
      };

      const vocabClass = Object.keys(containerIdMap)
        .find(cls => vocabElement.classList.contains(cls)) || 'vocab-box';

      const containerId = containerIdMap[vocabClass];
      const checkDeleteButton = document.getElementById(containerId);

      if (checkDeleteButton) {
        cleanUpDeleteButton(containerId);
      } else {
        const vocabDeleteContainer = document.createElement('div');
        vocabDeleteContainer.id = containerId;
        document.body.appendChild(vocabDeleteContainer);
        setTimeout(() => {
          ReactDOM.render(
            React.createElement(DeleteVocabDisplay, { containerId, editor, vocabElement }),
            vocabDeleteContainer,
          );
        }, 100);
      }
    },
  },
  glossaryTerms: [] as IGlossaryTerm[],
  icon,
  locale: 'en' as Locale,
  name: 'Vocab Box' as const,
};

export const vocabBoxPluginCommand = () => {
  FE.DefineIcon(VOCAB_BOX.commandName, { ICON: VOCAB_BOX.icon, template: FROALA_TEMPLATE_NAMES.empty });

  FE.RegisterCommand(VOCAB_BOX.commandName, {
    focus: false,
    icon: VOCAB_BOX.commandName,
    options: {
      'vocab-box': 'Vocab Box',
      // eslint-disable-next-line sort-keys
      'lesson-opener-cards': 'Lesson Opener Cards',
      'lesson-opener-list': 'Lesson Opener List (Lesson)',
      'lesson-opener-list-academic': 'Lesson Opener List (Academic)',
    },
    refreshAfterCallback: true,
    title: 'Insert Vocab Box',
    type: 'dropdown',
    undo: true,
  });
};
